<!-- Purchase returns: each purchase separately, purchases map, averages by purchase month and year. -->
<script lang="ts">
  import * as E from "../core/engine";
  import type { PurchaseRow } from "../core/types";
  import { app, need } from "../lib/state.svelte";
  import { download, toCsv } from "../lib/actions";
  import { esc, fmtDate, fmtNum, heat, monthLabel, pctPlain } from "../lib/format";
  import { baseOption, palette, timeAxis, tipMoney, valueAxis, type TipParam } from "../lib/chart";
  import ViewHead from "../components/ViewHead.svelte";
  import Kpi from "../components/Kpi.svelte";
  import Card from "../components/Card.svelte";
  import Chart from "../components/Chart.svelte";
  import Money from "../components/Money.svelte";
  import Pct from "../components/Pct.svelte";
  import PurchaseTable from "./parts/PurchaseTable.svelte";

  const m = $derived(need(app.model));
  const all = $derived(E.purchaseAnalysis(m, app.accList));
  const keys = $derived([...new Map(all.map((p) => [p.key, p.name])).entries()]);
  const sel = $derived(app.ui.purchaseKey && keys.some(([k]) => k === app.ui.purchaseKey) ? app.ui.purchaseKey : "*");
  const list = $derived(sel === "*" ? all : all.filter((p) => p.key === sel));
  const cost = $derived(list.reduce((s, p) => s + p.costPLN, 0));
  const valueNow = $derived(list.reduce((s, p) => s + (p.retNow !== null ? p.costPLN * (1 + p.retNow) : 0), 0));
  const winners = $derived(list.filter((p) => (p.retNow ?? 0) > 0).length);
  const best = $derived([...list].filter((p) => p.retNow !== null).sort((a, b) => (b.retNow as number) - (a.retNow as number)));
  const H = E.HORIZONS;

  // amount-weighted averages by purchase year and horizon
  interface YearAgg { n: number; cost: number; hz: Record<string, [number, number]>; now: [number, number] }
  const byYear = $derived.by(() => {
    const out: Record<string, YearAgg> = {};
    for (const p of list) {
      const y = p.day.slice(0, 4);
      const o = (out[y] ||= { n: 0, cost: 0, hz: {}, now: [0, 0] });
      o.n++; o.cost += p.costPLN;
      for (const h of H) {
        const v = p.hz[h.k];
        if (v !== null) { const a = (o.hz[h.k] ||= [0, 0]); a[0] += v * p.costPLN; a[1] += p.costPLN; }
      }
      if (p.retNow !== null) { o.now[0] += p.retNow * p.costPLN; o.now[1] += p.costPLN; }
    }
    return Object.entries(out).sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
  });
  const hzAvg = (o: YearAgg, k: string): number | null => (o.hz[k] ? o.hz[k][0] / o.hz[k][1] : null);

  const mapOpt = $derived.by(() => {
    const p = palette();
    const maxCost = Math.max(1, ...list.map((x) => x.costPLN));
    return {
      ...baseOption(p), legend: { show: false },
      tooltip: { ...baseOption(p).tooltip, trigger: "item", formatter: (x: TipParam) => { const q = x.data.q as PurchaseRow; return `<b>${esc(q.name)}</b><br>${fmtDate(q.day)} · ${esc(q.account)}<br>koszt ${tipMoney(q.costPLN)}<br>zwrot: <b>${pctPlain(q.retNow)}</b>${q.annNow !== null ? ` · ${pctPlain(q.annNow)}/rok` : ""}`; } },
      xAxis: timeAxis(p), yAxis: valueAxis((v) => fmtNum(v * 100, 0) + "%", p),
      series: [{ type: "scatter", data: list.filter((q) => q.retNow !== null).map((q) => ({ value: [q.day, q.retNow], q, symbolSize: 7 + 18 * Math.sqrt(q.costPLN / maxCost), itemStyle: { color: (q.retNow as number) >= 0 ? p.gain : p.loss, opacity: 0.85, borderColor: p.surface, borderWidth: 1.5 } })),
        markLine: { silent: true, symbol: "none", lineStyle: { color: p.axis, type: "solid" }, data: [{ yAxis: 0 }], label: { show: false } } }],
    };
  });
  const monthOpt = $derived.by(() => {
    const p = palette();
    const byMonth: Record<string, [number, number]> = {};
    for (const q of list) if (q.retNow !== null) { const k = q.day.slice(0, 7); const a = (byMonth[k] ||= [0, 0]); a[0] += q.retNow * q.costPLN; a[1] += q.costPLN; }
    const months = Object.keys(byMonth).sort();
    return {
      ...baseOption(p), legend: { show: false },
      tooltip: { ...baseOption(p).tooltip, trigger: "item", formatter: (x: TipParam) => `${x.name}<br>zwrot ${pctPlain(x.value)}<br>kwota ${tipMoney(byMonth[x.name][1])}` },
      xAxis: { type: "category", data: months, axisLabel: { color: p.muted, formatter: (v: string) => monthLabel(v) }, axisTick: { show: false }, axisLine: { lineStyle: { color: p.axis } } },
      yAxis: valueAxis((v) => fmtNum(v * 100, 0) + "%", p),
      series: [{ type: "bar", barMaxWidth: 18, data: months.map((k) => { const v = byMonth[k][0] / byMonth[k][1]; return { value: v, itemStyle: { color: v >= 0 ? p.gain : p.loss, borderRadius: v >= 0 ? [4, 4, 0, 0] : [0, 0, 4, 4] } }; }) }],
    };
  });

  function exportCsv() {
    download("zakupy.csv", toCsv<PurchaseRow>(list, [
      { k: "day", label: "data" }, { k: "account", label: "rachunek" }, { k: "symbol" }, { k: "name", label: "nazwa" }, { k: "qty", label: "ilosc" }, { k: "price", label: "cena" }, { k: "priceCcy", label: "waluta" },
      { label: "koszt_pln", v: (x) => x.costPLN.toFixed(2) }, ...H.map((h) => ({ label: "zwrot_" + h.k, v: (x: PurchaseRow) => { const v = x.hz[h.k]; return v === null ? "" : v.toFixed(4); } })),
      { label: "zwrot_dzis", v: (x) => x.retNow?.toFixed(4) ?? "" }, { label: "zwrot_roczny", v: (x) => x.annNow?.toFixed(4) ?? "" },
    ]));
  }
</script>

<ViewHead title="Opłacalność zakupów" sub="każdy zakup osobno: ile zyskał do dziś i po 1, 3, 6 miesiącach, roku, 2–5 latach (PLN, z prowizjami i zmianą kursu walut)" />
<div class="row"><select id="purchKey" value={sel} onchange={(ev) => (app.ui.purchaseKey = ev.currentTarget.value)}><option value="*">Wszystkie instrumenty</option>{#each keys as [k, n] (k)}<option value={k}>{n}</option>{/each}</select></div>
<div class="kpis">
  <Kpi label="Liczba zakupów" value={String(list.length)} sub="{winners} na plusie · {list.length - winners} na minusie" />
  <Kpi label="Łącznie zainwestowano" sub="suma kosztów zakupów">{#snippet value()}<Money v={cost} />{/snippet}</Kpi>
  <Kpi label="Te zakupy są dziś warte">
    {#snippet value()}<Money v={valueNow} />{/snippet}
    {#snippet sub()}<Pct v={cost > 0 ? valueNow / cost - 1 : null} /> (bez sprzedaży){/snippet}
  </Kpi>
  {#if best.length}
    {@const b0 = best[0]}
    {@const b1 = best[best.length - 1]}
    <Kpi label="Najlepszy zakup" sub="{fmtDate(b0.day)} · {b0.name}">{#snippet value()}<Pct v={b0.retNow} />{/snippet}</Kpi>
    <Kpi label="Najsłabszy zakup" sub="{fmtDate(b1.day)} · {b1.name}">{#snippet value()}<Pct v={b1.retNow} />{/snippet}</Kpi>
  {/if}
</div>
<div class="grid g2">
  <Card title="Mapa zakupów: zwrot do dziś" hint="oś X — data zakupu, oś Y — zwrot, wielkość — kwota"><Chart id="chMap" class="tall" option={mapOpt} /></Card>
  <Card title="Średni zwrot zakupów wg miesiąca zakupu" hint="ważony kwotą zakupu"><Chart id="chMonth" class="tall" option={monthOpt} /></Card>
</div>
<Card title="Średnia opłacalność wg roku zakupu" hint="średnia ważona kwotą; pusta komórka = horyzont jeszcze nie minął">
  <!-- {#key}: tables rebuilt from scratch when the filter changes (as in the old view) — with in-place updates Chrome may lay out columns differently (by fractions of a pixel) -->
  {#key list}<div class="table-wrap"><table class="heat"><thead><tr><th class="l">Rok zakupu</th><th>Zakupów</th><th>Kwota</th>{#each H as h (h.k)}<th>{h.k}</th>{/each}<th>Dziś</th></tr></thead>
    <tbody>
      {#each byYear as [y, o] (y)}
        {@const now = o.now[1] ? o.now[0] / o.now[1] : null}
        <tr><td class="l"><b>{y}</b></td><td>{o.n}</td><td><Money v={o.cost} /></td>
          {#each H as h (h.k)}{@const v = hzAvg(o, h.k)}<td class="cell" style={heat(v)}>{#if v === null}<span class="muted">·</span>{:else}{pctPlain(v)}{/if}</td>{/each}
          <td class="cell" style={heat(now, 0.5)}><b>{now !== null ? pctPlain(now) : "–"}</b></td></tr>
      {/each}
    </tbody>
  </table></div>{/key}
</Card>
<Card title="Wszystkie zakupy">
  {#snippet extra()}<button class="btn" id="expPurch" onclick={exportCsv}>Eksport CSV</button>{/snippet}
  {#key list}<PurchaseTable {list} withName={true} />{/key}
</Card>
